/**
 * PartnerDetailView - Full-page view for partner details
 */

import React, { useState, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Divider,
} from "@mui/material";
import { ArrowBack, ShareOutlined } from "@mui/icons-material";
import { Tag, Space, Table, Typography as AntTypography } from "antd";
import { downloadPartnerAgreementPdf, getPartner } from "../services/partnerService";
import { getDrivers } from "../../drivers/services/driverService";
import { getVehicles } from "../../vehicles/services/vehicleService";
import { getPaymentTerms } from "../../providers/services/paymentTermsService";
import PaymentTermsForm from "../../providers/components/PaymentTermsForm";
import PaymentTermsDisplay from "../../providers/components/PaymentTermsDisplay";
import SharePartnerJobsModal from "../components/SharePartnerJobsModal.jsx";
import {
  bankFromPartner,
  hasBankDetails,
  maskAccountNumber,
} from "../utils/providerProfileForm.js";

const { Title, Text } = AntTypography;

/**
 * PartnerDetailView Component
 */
const PartnerDetailView = () => {
  const { partnerId } = useParams();
  const navigate = useNavigate();

  // State
  const [partner, setPartner] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState(null);
  const [paymentTermsSource, setPaymentTermsSource] = useState(null);
  const [paymentTermsLoading, setPaymentTermsLoading] = useState(false);
  const [paymentTermsFormOpen, setPaymentTermsFormOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareJobsOpen, setShareJobsOpen] = useState(false);
  const [shareSuccessMessage, setShareSuccessMessage] = useState(null);

  // Fetch payment terms
  const fetchPaymentTerms = useCallback(async () => {
    if (!partnerId) return;
    setPaymentTermsLoading(true);
    try {
      const result = await getPaymentTerms(partnerId);
      if (result.success) {
        setPaymentTerms(result.data);
        setPaymentTermsSource(result.source);
      }
    } catch (err) {
      console.error("Failed to fetch payment terms:", err);
    } finally {
      setPaymentTermsLoading(false);
    }
  }, [partnerId]);

  // Fetch partner data
  const fetchPartnerData = useCallback(async () => {
    if (!partnerId) return;

    setLoading(true);
    setError(null);
    try {
      // Fetch partner
      const partnerResult = await getPartner(partnerId);
      if (partnerResult.success) {
        setPartner(partnerResult.data);

        // Fetch associated drivers
        const driversResult = await getDrivers({ partnerId, isActive: true });
        if (driversResult.success) {
          setDrivers(driversResult.data || []);
        }

        // Fetch associated vehicles
        const vehiclesResult = await getVehicles({ partnerId });
        if (vehiclesResult.success) {
          setVehicles(vehiclesResult.data || []);
        }
      } else {
        setError(partnerResult.error || "Failed to fetch partner");
      }
    } catch (err) {
      setError(err.message || "Failed to fetch partner data");
    } finally {
      setLoading(false);
    }
  }, [partnerId]);

  useEffect(() => {
    fetchPartnerData();
    fetchPaymentTerms();
  }, [fetchPartnerData, fetchPaymentTerms]);

  // Handlers
  const handleBack = useCallback(() => {
    navigate("/partners");
  }, [navigate]);

  // Driver table columns
  const driverColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name) => <Text strong>{name || "N/A"}</Text>,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (phone) => <Text type="secondary">{phone || "N/A"}</Text>,
    },
    {
      title: "License Number",
      dataIndex: "licenseNumber",
      key: "licenseNumber",
      render: (license) => <Text code>{license || "N/A"}</Text>,
    },
    {
      title: "License Expiry",
      dataIndex: "licenseExpiry",
      key: "licenseExpiry",
      render: (expiry) => {
        if (!expiry) return "N/A";
        const date = new Date(expiry);
        const isExpired = date < new Date();
        return (
          <Text type={isExpired ? "danger" : "secondary"}>
            {date.toLocaleDateString()}
            {isExpired && <Tag color="red" style={{ marginLeft: 8 }}>Expired</Tag>}
          </Text>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive) => (
        <Tag color={isActive ? "green" : "red"}>{isActive ? "Active" : "Inactive"}</Tag>
      ),
    },
  ];

  // Vehicle table columns
  const vehicleColumns = [
    {
      title: "License Plate",
      dataIndex: "licensePlate",
      key: "licensePlate",
      render: (plate) => <Text strong code>{plate || "N/A"}</Text>,
    },
    {
      title: "Make & Model",
      key: "makeModel",
      render: (_, record) => (
        <Text>
          {record.make} {record.model} ({record.year})
        </Text>
      ),
    },
    {
      title: "Type",
      dataIndex: "vehicleType",
      key: "vehicleType",
      render: (type) => (
        <Tag color={type === "large" ? "red" : type === "medium" ? "orange" : "blue"}>
          {type?.toUpperCase() || "N/A"}
        </Tag>
      ),
    },
    {
      title: "Capacity",
      dataIndex: "capacity",
      key: "capacity",
      render: (capacity) => <Text>{capacity ? `${capacity} kg` : "N/A"}</Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const colorMap = {
          available: "green",
          in_use: "blue",
          maintenance: "orange",
          unavailable: "red",
        };
        return (
          <Tag color={colorMap[status] || "default"}>
            {status?.replace("_", " ").toUpperCase() || "N/A"}
          </Tag>
        );
      },
    },
  ];

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        p={{ xs: 2, md: 3 }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={{ xs: 2, md: 3 }}>
        <Alert severity="error">Error: {error}</Alert>
        <Box mt={2}>
          <Button startIcon={<ArrowBack />} onClick={handleBack}>
            Back to Partners
          </Button>
        </Box>
      </Box>
    );
  }

  if (!partner) {
    return (
      <Box p={{ xs: 2, md: 3 }}>
        <Alert severity="warning">Partner not found</Alert>
        <Box mt={2}>
          <Button startIcon={<ArrowBack />} onClick={handleBack}>
            Back to Partners
          </Button>
        </Box>
      </Box>
    );
  }

  const bankSummary = bankFromPartner(partner);
  const showBankSummary = hasBankDetails(partner);

  return (
    <Box>
      {/* Header */}
      <Box mb={{ xs: 2, md: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBack}
          size="small"
          sx={{ whiteSpace: "nowrap", mb: 1 }}
        >
          Back to Partners
        </Button>

        <Box
          display="flex"
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          gap={{ xs: 1.5, sm: 2 }}
          flexDirection={{ xs: "column", sm: "row" }}
        >
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            flexWrap="wrap"
            sx={{ width: { xs: "100%", sm: "auto" }, minWidth: 0, flex: 1 }}
          >
            <Title
              level={2}
              style={{
                margin: 0,
                fontSize: "clamp(1.25rem, 4vw, 1.5rem)",
                lineHeight: 1.3,
                overflowWrap: "break-word",
                wordBreak: "normal",
              }}
            >
              {partner.name}
            </Title>
            <Tag color={partner.isActive ? "green" : "red"} style={{ flexShrink: 0 }}>
              {partner.isActive ? "Active" : "Inactive"}
            </Tag>
          </Box>
          <Space wrap>
            <Button
              variant="outlined"
              onClick={() => downloadPartnerAgreementPdf(partnerId)}
              size="small"
              sx={{ whiteSpace: "nowrap" }}
            >
              Agreement PDF
            </Button>
            <Button
              variant="contained"
              startIcon={<ShareOutlined />}
              onClick={() => setShareJobsOpen(true)}
              size="small"
              sx={{
                whiteSpace: "nowrap",
                width: { xs: "100%", sm: "auto" },
                flexShrink: 0,
              }}
            >
              Share upcoming jobs
            </Button>
          </Space>
        </Box>
      </Box>

      <Grid container spacing={{ xs: 2, md: 3 }}>
        {/* Left Column - Partner Info */}
        <Grid item xs={12} md={8}>
          {/* Partner Information */}
          <Card sx={{ mb: { xs: 2, md: 3 } }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Typography variant="h6" gutterBottom>
                Partner Information
              </Typography>
              <Divider sx={{ my: { xs: 1.5, md: 2 } }} />
              <Grid container spacing={{ xs: 1.5, md: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Contact Person
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {partner.contactPerson || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {partner.phone || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {partner.email || "N/A"}
                  </Typography>
                </Grid>
                {partner.address && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Address
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {partner.address}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {showBankSummary && (
            <Card sx={{ mb: { xs: 2, md: 3 } }}>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h6" gutterBottom>
                  Bank details
                </Typography>
                <Divider sx={{ my: { xs: 1.5, md: 2 } }} />
                <Grid container spacing={{ xs: 1.5, md: 2 }}>
                  {bankSummary.bankName && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Bank
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {bankSummary.bankName}
                      </Typography>
                    </Grid>
                  )}
                  {bankSummary.accountHolder && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Account holder
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {bankSummary.accountHolder}
                      </Typography>
                    </Grid>
                  )}
                  {bankSummary.accountNumber && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Account number
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {maskAccountNumber(bankSummary.accountNumber)}
                      </Typography>
                    </Grid>
                  )}
                  {bankSummary.branchCode && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Branch code
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {bankSummary.branchCode}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Payment Terms */}
          <Card sx={{ mb: { xs: 2, md: 3 } }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                mb={{ xs: 1.5, md: 2 }}
                flexDirection={{ xs: "column", sm: "row" }}
                gap={1}
              >
                <Typography variant="h6">Payment Terms</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setPaymentTermsFormOpen(true)}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  {paymentTerms && paymentTermsSource === "direct" ? "Edit" : "Configure"}
                </Button>
              </Box>
              <Divider sx={{ my: { xs: 1.5, md: 2 } }} />
              {paymentTermsLoading ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : paymentTerms ? (
                <PaymentTermsDisplay
                  paymentTerms={paymentTerms}
                  source={paymentTermsSource}
                  inheritedFrom={null}
                />
              ) : (
                <Alert severity="info">
                  No payment terms configured. Drivers will use default terms.
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Associated Drivers */}
          <Card sx={{ mb: { xs: 2, md: 3 } }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Typography variant="h6" gutterBottom>
                Associated Drivers ({drivers.length})
              </Typography>
              <Divider sx={{ my: { xs: 1.5, md: 2 } }} />
              {drivers.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No drivers associated with this partner
                </Typography>
              ) : (
                <Box sx={{ overflowX: "auto" }}>
                  <Table
                    columns={driverColumns}
                    dataSource={drivers}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    scroll={{ x: "max-content" }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Associated Vehicles */}
          <Card>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Typography variant="h6" gutterBottom>
                Associated Vehicles ({vehicles.length})
              </Typography>
              <Divider sx={{ my: { xs: 1.5, md: 2 } }} />
              {vehicles.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No vehicles associated with this partner
                </Typography>
              ) : (
                <Box sx={{ overflowX: "auto" }}>
                  <Table
                    columns={vehicleColumns}
                    dataSource={vehicles}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    scroll={{ x: "max-content" }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Statistics */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Typography variant="h6" gutterBottom>
                Statistics
              </Typography>
              <Divider sx={{ my: { xs: 1.5, md: 2 } }} />
              <Box display="flex" flexDirection="column" gap={{ xs: 1.5, md: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Drivers
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {drivers.length}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Active Drivers
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {drivers.filter((d) => d.isActive).length}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Vehicles
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {vehicles.length}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Available Vehicles
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {vehicles.filter((v) => v.status === "available").length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Payment Terms Form Modal */}
      <PaymentTermsForm
        open={paymentTermsFormOpen}
        onClose={() => setPaymentTermsFormOpen(false)}
        providerId={partnerId}
        providerName={partner?.name}
        existingTerms={paymentTerms && paymentTermsSource === "direct" ? paymentTerms : null}
        onSave={() => {
          fetchPaymentTerms();
          setPaymentTermsFormOpen(false);
        }}
      />

      <SharePartnerJobsModal
        open={shareJobsOpen}
        partner={partner}
        onClose={() => setShareJobsOpen(false)}
        onShared={({ message }) => {
          setShareSuccessMessage(message || "Schedule shared successfully");
        }}
      />

      <Snackbar
        open={Boolean(shareSuccessMessage)}
        autoHideDuration={5000}
        onClose={() => setShareSuccessMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShareSuccessMessage(null)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {shareSuccessMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PartnerDetailView;

